import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, Camera, CheckCircle, X, Save, Trash2, Users, ArrowLeft } from 'lucide-react';
import Cropper from 'react-easy-crop';
import { useUser } from '../contexts/UserContext';
import { getToken } from '../utils/auth';
import LoadingScreen from '../components/LoadingScreen';

const API_BASE = import.meta.env.VITE_API_BASE;

export default function UserSettings() {
  const { userData: contextUserData, setUserData: setContextUserData } = useUser();
  const [userData, setUserData] = useState({
    fullName: '',
    avatar: null,
    company: {
      name: '',
      logoUrl: null
    },
    email: '',
    phone: '',
    phoneCountry: '',
    teamName: null
  });
  const [displayInfo, setDisplayInfo] = useState({
    fullName: '',
    company: {
      name: '',
      logoUrl: null
    },
    email: '',
    phone: '',
    phoneCountry: ''
  });
  const [showCompanyName, setShowCompanyName] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [showCropper, setShowCropper] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [shouldDeleteAvatar, setShouldDeleteAvatar] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (contextUserData) {
      const newData = {
        fullName: contextUserData.fullName || '',
        avatar: contextUserData.avatarUrl || null,
        company: {
          name: contextUserData.company?.name || '',
          logoUrl: contextUserData.company?.logoUrl || null
        },
        email: contextUserData.email || '',
        phone: contextUserData.phone || '',
        phoneCountry: contextUserData.phoneCountry || '',
        teamName: contextUserData.teamName || null
      };
      setUserData(newData);
      setDisplayInfo(newData);
      setOriginalData(newData);
      setShowCompanyName(
        contextUserData.company?.name && 
        contextUserData.company.name !== contextUserData.fullName
      );
      setLoading(false);
    } else {
      navigate('/login');
    }
  }, [contextUserData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user types
    setErrors(prev => ({ ...prev, [name]: '' }));
    setSuccess('');
  };

  const handleAvatarClick = () => {
    // Reset file input before clicking to ensure onChange fires
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setShouldDeleteAvatar(false); // Reset delete flag when uploading new image
      const reader = new FileReader();
      reader.onload = () => {
        setImageUrl(reader.result);
        setShowCropper(true);
        setZoom(1);
        setCrop({ x: 0, y: 0 });
        setCroppedAreaPixels(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCrop = async () => {
    if (!croppedAreaPixels) return;

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const image = new Image();
      
      image.src = imageUrl;
      await new Promise((resolve) => {
        image.onload = resolve;
      });

      // Set canvas size to match the cropped dimensions while maintaining max dimensions
      const maxSize = 600;
      let width = 200;  // Keep avatar size at 200x200 since it's a profile picture
      let height = 200;
      
      canvas.width = width;
      canvas.height = height;

      // Draw the cropped image
      const { x, y } = croppedAreaPixels;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      ctx.drawImage(
        image,
        x * scaleX,
        y * scaleY,
        croppedAreaPixels.width * scaleX,
        croppedAreaPixels.height * scaleY,
        0,
        0,
        width,
        height
      );

      // Convert to blob
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
          setUserData(prev => ({ ...prev, avatar: file }));
          setShowCropper(false);
          setImageUrl('');
          setCroppedAreaPixels(null);
          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      }, 'image/jpeg', 0.9);
    } catch (error) {
      // Error handling is still in place, just not logging to console
    }
  };

  const handleDeleteAvatar = () => {
    setUserData(prev => ({ ...prev, avatar: null }));
    setShouldDeleteAvatar(true);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validate = () => {
    const newErrors = {};
    let isValid = true;

    // Validate fullName (required)
    if (!userData.fullName) {
      newErrors.fullName = 'Full name is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const hasChanges = () => {
    if (!originalData) return false;
    return JSON.stringify({
      fullName: userData.fullName,
    }) !== JSON.stringify({
      fullName: originalData.fullName,
    }) || userData.avatar !== originalData.avatar;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || !validate() || !hasChanges()) return;
    setSaving(true);
    setErrors({});
    setSuccess('');

    try {
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      // If avatar should be deleted and no new avatar is being uploaded
      if (shouldDeleteAvatar && !(userData.avatar instanceof File)) {
        const deleteResponse = await fetch(`${API_BASE}/user/avatar`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!deleteResponse.ok) {
          throw new Error('Failed to delete avatar');
        }
      }

      // Always use FormData as backend expects multipart/form-data
      const formData = new FormData();
      
      // Add avatar if it's a new file
      if (userData.avatar instanceof File) {
        formData.append('avatar', userData.avatar);
      }
      
      // Add fullName if changed
      if (userData.fullName !== originalData.fullName) {
        formData.append('fullName', userData.fullName);
      }

      const response = await fetch(`${API_BASE}/user`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        // Fetch updated user data
        const userResponse = await fetch(`${API_BASE}/user`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (userResponse.ok) {
          const updatedData = await userResponse.json();
          setContextUserData(updatedData);
          setSuccess('User profile updated successfully');

        }
      } else if (response.status === 401) {
        navigate('/login');
      } else {
        const data = await response.json();
        setErrors({ general: data.message || 'Failed to update user details' });
      }
    } catch (err) {
      setErrors({ general: err.message === 'Failed to fetch' ? 'Connection error. Please check your internet connection and try again.' : (err.message || 'Unable to connect to the server') });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="max-w-2xl mx-auto pb-8">
      <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-8">User Profile</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Avatar and Contact Info Section */}
        <div className="flex items-start space-x-6">
          <div className="flex flex-col items-center gap-2 self-center">
            <div 
              className="w-32 h-32 rounded-full bg-[var(--card-bg)] overflow-hidden shadow-lg relative group border-[0.5px] border-white/10"
            >
              {userData.avatar ? (
                <>
                  <img
                    src={typeof userData.avatar === 'string' ? userData.avatar : URL.createObjectURL(userData.avatar)}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={handleDeleteAvatar}
                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 p-1.5 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-black/70"
                  >
                    <Trash2 className="w-[18px] h-[18px] text-white" />
                  </button>
                </>
              ) : (
                <div 
                  onClick={handleAvatarClick}
                  className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-[var(--hover-light)] transition-colors duration-200"
                >
                  <Camera className="w-8 h-8 text-[var(--text-secondary)] mb-2" />
                  <span className="text-sm text-[var(--text-secondary)]">Upload Photo</span>
                </div>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              className="hidden"
            />
          </div>

          <div className="flex flex-col h-28 pt-1">
            {/* Company Section */}
            {showCompanyName ? (
              <div className="flex items-center gap-3 mb-4">
                {displayInfo.company?.logoUrl && (
                  <img 
                    src={displayInfo.company.logoUrl} 
                    alt="Company Logo" 
                    className="w-16 h-16 object-contain p-0.5 border-[0.05px] border-gray-500/20 rounded-md bg-white/5"
                  />
                )}
                <span className="text-white font-medium text-lg">
                  {displayInfo.company.name}
                </span>
              </div>
            ) : null}

            {/* User Info */}
            <div className={`flex flex-col gap-1 text-black/80 ${!showCompanyName ? 'mt-6' : 'mt-auto'}`}>
              <span className="text-sm">{displayInfo.fullName}</span>
              {displayInfo.email && (
                <span className="text-sm text-[var(--text-secondary)]">{displayInfo.email}</span>
              )}
              {displayInfo.phone && (
                <span className="text-sm text-[var(--text-secondary)]">
                  {displayInfo.phoneCountry && `+${displayInfo.phoneCountry} `}{displayInfo.phone}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-6 mt-16">
          {/* Full Name */}
          <div className="pt-8">
            <label htmlFor="fullName" className="block text-sm font-medium text-[var(--text-primary)] mb-1">
              Full Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-[var(--text-secondary)]" />
              </div>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={userData.fullName}
                onChange={handleChange}
                className={`w-full pl-10 pr-3 py-2 bg-[var(--card-bg)] border ${
                  errors.fullName ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-[var(--border-color)] focus:ring-[var(--accent-primary)] focus:border-[var(--accent-primary)]'
                } rounded-md text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:ring-1`}
                placeholder="Enter your full name"
              />
            </div>
            {errors.fullName && (
              <p className="mt-1 text-sm text-red-500">{errors.fullName}</p>
            )}
          </div>
          
          {/* Team Name - Only shown if not null */}
          {userData.teamName && (
            <div className="pt-4">
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                Team
              </label>
              <div className="relative">
                <div className="flex items-center mt-1">
                  <span className="inline-flex items-center px-4 py-2 text-sm font-medium border-l-3 border-blue-500 bg-blue-50 text-blue-600 transition-all duration-200 hover:bg-blue-100 rounded-[2px]">
                    <span className="mr-1.5 text-blue-500 font-semibold">@</span>
                    {userData.teamName}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-4 pt-6 mt-8">
          <div className="flex items-center justify-between">
          <button
                type="button"
                onClick={() => navigate('/home')}
                className="px-5 py-2 bg-[var(--card-bg)] hover:bg-[var(--hover-light)] active:bg-[var(--hover-dark)] rounded-md text-[var(--text-primary)] font-medium shadow-sm transition-all duration-200 flex items-center gap-2"
              >
                <ArrowLeft size={16} className="stroke-[1.8]" />
                Return
              </button>
            <button
              type="submit"
              disabled={saving || !hasChanges()}
              className={`flex items-center px-5 py-2 rounded-md font-medium shadow-sm transition-all duration-200 ${
                saving || !hasChanges()
                  ? 'bg-[var(--card-bg)] text-[var(--text-secondary)] opacity-50 cursor-not-allowed'
                  : 'bg-[var(--accent-primary)] text-white hover:bg-[var(--accent-primary-hover)] active:bg-[var(--accent-primary-pressed)]'
              }`}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>

          <div className="min-h-[80px] pt-2">
            {errors.general && (
              <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                {errors.general}
              </div>
            )}

            {success && (
              <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                {success}
              </div>
            )}
          </div>
        </div>
      </form>

      {/* Image Cropper Modal */}
      {showCropper && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[var(--card-bg)] p-4 rounded-lg max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-[var(--text-primary)]">Edit Profile Image</h3>
              <button
                onClick={() => {
                  setShowCropper(false);
                  setImageUrl('');
                }}
                className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4 relative h-[300px] bg-black/30 rounded-md overflow-hidden">
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="round"
                showGrid={false}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="zoom" className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                Zoom
              </label>
              <input
                type="range"
                id="zoom"
                min="1"
                max="3"
                step="0.1"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowCropper(false);
                  setImageUrl('');
                }}
                className="px-4 py-2 text-[var(--text-primary)] hover:text-[var(--text-secondary)] rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleCrop}
                className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded hover:bg-[var(--hover-dark)]"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 